/**
 * EMAIL_RULES editor (contacts §4A.3-bis; handoff §2.3) — standing
 * user-defined rules over the runtime's EMAIL_RULES.md store, via the
 * ADOPTED gateway face `email.rules_get` / `email.rules_set` (whole-list;
 * 2026-07-16). Add/edit/toggle/remove; every save restates each CHANGED
 * rule's exact effect for confirmation; shows per-rule provenance +
 * last-execution from the audit trail. Real store lands with
 * `[M:email-E0-bake]` — until then the pending-backend banner shows.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { diffRules, newRuleId, restateRule, ruleViolation, type RuleChange } from "@/lib/email-rules-logic";
import type { EmailRule } from "@/types/contacts";

function methodPending(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

const CHANGE_LABEL: Record<RuleChange["kind"], string> = {
  added: "NOVA",
  edited: "ALTERADA",
  enabled: "ATIVADA",
  disabled: "DESATIVADA",
  removed: "REMOVIDA",
};

export function EmailRulesEditor() {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);

  const [saved, setSaved] = useState<EmailRule[] | null>(null);
  const [draft, setDraft] = useState<EmailRule[]>([]);
  const [backendPending, setBackendPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<RuleChange[] | null>(null);

  const load = useCallback(() => {
    if (!rpc || connectionState !== "connected") return;
    rpc
      .emailRulesGet()
      .then((res) => {
        setSaved(res.rules);
        setDraft(res.rules);
        setBackendPending(false);
      })
      .catch((err: unknown) => {
        if (methodPending(err)) setBackendPending(true);
        else setError(err instanceof Error ? err.message : String(err));
      });
  }, [rpc, connectionState]);
  useEffect(() => load(), [load]);

  const changes = saved ? diffRules(saved, draft) : [];
  const violations = draft.map((r) => ruleViolation(r));
  const hasViolation = violations.some(Boolean);

  function patchRule(i: number, p: Partial<EmailRule>) {
    setDraft((d) => d.map((r, j) => (j === i ? { ...r, ...p } : r)));
  }

  async function applySave() {
    if (!rpc) return;
    try {
      const res = await rpc.emailRulesSet({ rules: draft });
      setSaved(res.rules);
      setDraft(res.rules);
      setConfirming(null);
    } catch (err) {
      setConfirming(null);
      if (methodPending(err)) setBackendPending(true);
      else setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="space-y-2" data-testid="email_rules_editor">
      <h4 className="text-sm font-semibold">
        {t("emailRules.title", "Regras de e-mail (instruções permanentes)")}
      </h4>
      <p className="text-xs text-slate-500">
        {t(
          "emailRules.subtitle",
          "“Quando X acontecer, faça Y.” Cada regra é uma aprovação permanente — por isso a confirmação repete exatamente o efeito dela.",
        )}
      </p>

      {backendPending && (
        <p className="rounded bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300" data-testid="email_rules_pending_backend">
          {t(
            "emailRules.pendingBackend",
            "O armazenamento de regras chega junto com a caixa de e-mail do assistente — em preparação no seu runtime.",
          )}
        </p>
      )}
      {error && <p className="rounded bg-red-500/10 p-2 text-xs text-red-600">{error}</p>}

      {draft.map((rule, i) => (
        <div key={rule.id} className="rounded border border-slate-200 p-2 dark:border-slate-800" data-testid={`email_rule_${rule.id}`}>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => patchRule(i, { enabled: e.target.checked })}
                data-testid={`email_rule_enabled_${i}`}
              />
              {t("emailRules.enabled", "ativa")}
            </label>
            {rule.provenance && (
              <span className="text-[10px] text-slate-400">{rule.provenance}</span>
            )}
            {rule.lastRunAt && (
              <span className="text-[10px] text-slate-400">
                {t("emailRules.lastRun", "última execução:")} {rule.lastRunAt}
              </span>
            )}
            <button
              type="button"
              onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
              className="ml-auto text-xs text-slate-400 hover:text-red-500"
              data-testid={`email_rule_remove_${i}`}
            >
              ✕
            </button>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
            <label className="block">
              <span className="mb-0.5 block text-xs text-slate-500">{t("emailRules.when", "Quando…")}</span>
              <input
                type="text"
                value={rule.when}
                onChange={(e) => patchRule(i, { when: e.target.value })}
                className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
                data-testid={`email_rule_when_${i}`}
              />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-xs text-slate-500">{t("emailRules.then", "Então…")}</span>
              <input
                type="text"
                value={rule.then}
                onChange={(e) => patchRule(i, { then: e.target.value })}
                className="w-full rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-700"
                data-testid={`email_rule_then_${i}`}
              />
            </label>
          </div>
          {violations[i] && <p className="mt-1 text-xs text-red-600">{violations[i]}</p>}
        </div>
      ))}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() =>
            setDraft((d) => [...d, { id: newRuleId(d), when: "", then: "", enabled: true }])
          }
          className="text-xs text-amber-600 hover:underline"
          data-testid="email_rule_add"
        >
          + {t("emailRules.add", "nova regra")}
        </button>
        <button
          type="button"
          disabled={backendPending || hasViolation || changes.length === 0}
          onClick={() => setConfirming(changes)}
          className="ml-auto rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          data-testid="email_rules_save"
        >
          {t("common.save", "Salvar")}
          {changes.length > 0 ? ` (${changes.length})` : ""}
        </button>
      </div>

      {/* restate-the-effect confirm (§4A.3-bis) */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="email_rules_confirm">
          <div className="max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-slate-900">
            <h5 className="mb-2 text-sm font-semibold">
              {t("emailRules.confirmTitle", "Confirme o efeito exato de cada mudança")}
            </h5>
            <ul className="mb-3 space-y-1 text-xs">
              {confirming.map((c) => (
                <li key={`${c.kind}:${c.rule.id}`}>
                  <span className="mr-1 rounded bg-slate-500/15 px-1 py-0.5 font-semibold">
                    {CHANGE_LABEL[c.kind]}
                  </span>
                  {restateRule(c.rule)}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="rounded border border-slate-300 px-3 py-1 text-xs dark:border-slate-700"
              >
                {t("common.cancel", "Cancelar")}
              </button>
              <button
                type="button"
                onClick={() => void applySave()}
                className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white"
                data-testid="email_rules_confirm_apply"
              >
                {t("common.confirm", "Confirmar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
